import { SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import _zip from "lodash/zip";
import { EvaChartStates } from "types/factory";

import { DataEnum, Format } from "models";
import { Annotation } from "models/chart";

import { createChartLocks } from "../locks";
import { DEFAULT_WIDTH, DEFAULT_Z_INDEX } from "./constants";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  formatChartTitleWithSubscript,
  getDefaultYAxisLogOptions,
  getTimeSeriesMinMax
} from "./utils";

function getMarkLineData(annotations: Annotation[], yMin: number) {
  if (!annotations) {
    return {};
  }
  const marklineData = annotations.map((item, index) => {
    return [
      {
        xAxis: item.xValue,
        yAxis: yMin,
        label: {
          show: true,
          formatter: () => item.comment
        }
      },
      {
        xAxis: item.xValue,
        y: `${100 + 25 * index}`
      }
    ];
  });
  return {
    lineStyle: {
      type: "dashed"
    },
    symbol: [],
    data: marklineData
  };
}

export function translateTrendDateResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    chartType,
    series,
    typeWellSeries = [],
    logAxis,
    overrideSeries = true
  } = states;

  const { xMin, xMax, yMin, yMax } = getTimeSeriesMinMax(series, Format.Year);
  const {
    lockedXMax,
    lockedXMin,
    lockedYMax,
    lockedYMin,
    lockedXMinType,
    lockedXMaxType
  } = createChartLocks({
    type: EvaChart.TrendDate,
    states
  });

  const xAxis: Partial<XAXisOption> = {
    name: formatChartTitleWithSubscript(layout.xAxis.title),
    nameGap: 27,
    type: "time",
    max: lockedXMax,
    min: lockedXMin
  };

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout?.yAxis?.title) || "",
    nameGap: 43,
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max: lockedYMax
  };

  if (logAxis?.properties?.y?.active) {
    const yAxisLogOptions = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.TrendDate,
      undefined,
      undefined
    );
    Object.assign(yAxis, yAxisLogOptions);
  }

  let options = createEntityState(ChartOption.Chart, {
    chartType,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        ...yAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: Math.floor(yMin.value),
            type: yMin.type,
            override: true // it manages if lock should be overridden or not
          },
          max: {
            value: Math.floor(yMax.value),
            type: yMax.type,
            override: true // it manages if lock should be overridden or not
          }
        },
        axisPointer: {
          label: {
            margin: SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
          }
        }
      })
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxis, {
        ...xAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: false, // useY is managed at hd chart
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: xMin.value,
            type: lockedXMinType ?? xMin.type,
            format: Format.Number,
            // lets XMinLocker save the 2000 default into locks so it shows as locked
            override: true
          },
          max: {
            value: xMax.value,
            type: xMax.type,
            format: Format.Number,
            // The default max is recomputed each render, so XMaxLocker must not save it
            override: lockedXMaxType !== DataEnum.DefaultDate
          }
        }
      })
    },
    grid: { top: 46, bottom: 46, left: 62, right: 20 },
    ...(overrideSeries && {
      series: series.map((location, index) => {
        const { label, style, x, y, groupTitle } = location;
        const data = _zip(x, y);
        const id =
          groupTitle !== label
            ? `${groupTitle}, ${label}, ${index}`
            : `${groupTitle}, ${index}`;

        const isPoint = data.length === 1;
        const multiPoint = data.length > 1;

        const lineSeries = createEntityState(ChartOption.LineSeries, {
          id,
          name: label,
          data: data,
          itemStyle: {
            color: style?.hexColor ?? "#000"
          },
          lineStyle: {
            width: style?.thickness ?? DEFAULT_WIDTH,
            color: style?.hexColor,
            type: location.isForecast
              ? location.isComparisonForecast
                ? "dotted"
                : "dashed"
              : "solid",
            opacity: 1
          },
          z: DEFAULT_Z_INDEX,
          ...(isPoint ? { symbolSize: 5 } : {}),
          ...(isPoint ? { symbol: "circle" } : {}),
          showSymbol: !multiPoint || isPoint,
          // TODO chart: add proper typing
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          markLine: getMarkLineData(
            location.annotations,
            logAxis?.properties?.y?.active ? 0.01 : 0
          )
        });

        return lineSeries;
      })
    })
  });
  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }
  if (series.length === 0 && typeWellSeries.length === 0) {
    options = {};
  }
  return options;
}
